/**
 * AP-29-E — Mandantenliste der Betreiber-Konsole.
 *
 * Die einzige Liste im ganzen ERP, die **nicht** nach `tenantId` filtert. Sie
 * liegt hinter dem `PlatformAdminGuard` des Backends; die Route hier prüft
 * zusätzlich `user.isOperator`, damit der Eintrag für niemanden sonst erscheint.
 *
 * Aufbau nach dem verbindlichen Listen-Standard (`docs/pattern-auswertungsmaske.md`):
 * FilterBar + konfigurierbare, sortier- und verschiebbare Spalten, Spaltenlayout
 * serverseitig je Benutzer (AP-23).
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Settings, Check, ArrowUpDown, ShieldCheck, ScrollText, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FilterBar } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import type { FilterFieldDef, ActiveFilter } from '@/components/list/list.types'
import { pageWide } from '@/lib/form-styles'
import { operatorApi, type OperatorTenantRow, type PaginatedTenants } from '../api/operator.api'
import { MandantAnlegen } from '../components/MandantAnlegen'

// ─── FilterBar-Felder ─────────────────────────────────────────────────────────

const AVAILABLE_FILTER_FIELDS: FilterFieldDef[] = [
  {
    key: 'search',
    label: 'Suche (Name, Subdomain)',
    type: 'text',
    category: 'core',
    operators: ['contains', 'not_contains'],
  },
  {
    key: 'approvalStatus',
    label: 'Freischaltung',
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: [
      { value: 'APPROVED', label: 'Freigeschaltet' },
      { value: 'PENDING', label: 'Wartet' },
      { value: 'REJECTED', label: 'Abgelehnt' },
    ],
  },
  { key: 'isActive', label: 'Aktiv', type: 'boolean', category: 'core', operators: ['is'] },
]

const DEFAULT_FILTER_FIELD_KEYS = ['search', 'approvalStatus']

// ─── Spalten ──────────────────────────────────────────────────────────────────

interface ColDef {
  key: string
  label: string
  align?: 'right'
}

const ALL_COLUMNS: ColDef[] = [
  { key: 'subdomain', label: 'Subdomain' },
  { key: 'approvalStatus', label: 'Freischaltung' },
  { key: 'modules', label: 'Module' },
  { key: 'moduleCount', label: 'Anzahl', align: 'right' },
  { key: 'activeUsers', label: 'Benutzer', align: 'right' },
  { key: 'plan', label: 'Tarif' },
  { key: 'createdAt', label: 'Angelegt' },
  { key: 'isActive', label: 'Aktiv' },
  { key: 'deletionRequestedAt', label: 'Löschantrag vom' },
]

const COLUMN_LAYOUT_KEY = 'columnbar_layout_operator_tenants'
const DEFAULT_COLUMN_KEYS = ['subdomain', 'approvalStatus', 'modules', 'activeUsers', 'createdAt']

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
}

function loadColumnState(): ColumnState {
  const parsed = loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null)
  if (parsed?.columnKeys?.length) {
    return { columnKeys: parsed.columnKeys, columnWidths: parsed.columnWidths ?? {} }
  }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {} }
}

// ─── Anzeige-Helfer ───────────────────────────────────────────────────────────

function ApprovalBadge({ status }: { status: OperatorTenantRow['approvalStatus'] }) {
  const cfg = {
    APPROVED: { label: 'Frei', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    PENDING: { label: 'Wartet', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
    REJECTED: { label: 'Abgelehnt', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  }[status] ?? { label: status, class: 'bg-muted text-muted-foreground' }

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.class}`}>
      {cfg.label}
    </span>
  )
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function OperatorTenantsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [anlegen, setAnlegen] = useState(false)

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(loadColumnState)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const dragKeyRef = useRef<string | null>(null)

  const [result, setResult] = useState<PaginatedTenants | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, currentPage: number) => {
      setLoading(true)
      setError(null)
      try {
        const params: Parameters<typeof operatorApi.listTenants>[0] = { page: currentPage, limit: 50 }
        for (const [key, active] of Object.entries(filters)) {
          if (!active.value) continue
          if (key === 'search') params.search = active.value
          if (key === 'approvalStatus') params.approvalStatus = active.value
          if (key === 'isActive') params.isActive = active.value === 'true'
        }
        setResult(await operatorApi.listTenants(params))
      } catch (err) {
        setError('Die Mandantenliste konnte nicht geladen werden.')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    fetchData(filterValues, page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleFiltersChange = useCallback(
    (newValues: Record<string, ActiveFilter>) => {
      setFilterValues(newValues)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => fetchData(newValues, 1), 400)
    },
    [fetchData],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(COLUMN_LAYOUT_KEY, next)
      return next
    })
  }, [])

  // ── Drag & Drop ───────────────────────────────────────────────────────────

  const handleDragStart = (key: string) => { dragKeyRef.current = key }
  const handleDragOver = (e: React.DragEvent, key: string) => { e.preventDefault(); setDragOverKey(key) }
  const handleDragEnd = () => { dragKeyRef.current = null; setDragOverKey(null) }
  const handleDrop = (targetKey: string) => {
    const srcKey = dragKeyRef.current
    if (!srcKey || srcKey === targetKey) { setDragOverKey(null); return }
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
    setDragOverKey(null)
  }

  // ── Spalten-Resize ────────────────────────────────────────────────────────

  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null)
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    resizeRef.current = { key, startX: e.clientX, startW }
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const delta = ev.clientX - resizeRef.current.startX
      const newW = Math.max(60, resizeRef.current.startW + delta)
      updateColState((prev) => ({
        ...prev,
        columnWidths: { ...prev.columnWidths, [resizeRef.current!.key]: newW },
      }))
    }
    const onUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys
    .map((k) => ALL_COLUMNS.find((c) => c.key === k))
    .filter((c): c is ColDef => c !== undefined)
  const availableToAdd = ALL_COLUMNS.filter((c) => !colState.columnKeys.includes(c.key))

  const renderCell = (row: OperatorTenantRow, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'subdomain': return <span className="font-mono text-xs">{row.subdomain}</span>
      case 'approvalStatus': return <ApprovalBadge status={row.approvalStatus} />
      case 'modules':
        return (
          <span className="text-xs text-muted-foreground">
            {row.modules.length ? row.modules.join(', ') : '—'}
            {row.hasTrial && (
              <span className="ml-1 rounded bg-yellow-100 px-1 text-[10px] font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                Test
              </span>
            )}
          </span>
        )
      case 'moduleCount': return row.moduleCount
      case 'activeUsers': return row.activeUsers
      case 'plan': return row.plan
      case 'createdAt': return new Date(row.createdAt).toLocaleDateString()
      case 'isActive': return row.isActive ? 'ja' : 'nein'
      case 'deletionRequestedAt':
        // Ohne Antrag bleibt die Zelle leer statt „—": In einer Liste, in der
        // fast jede Zeile leer ist, soll die eine gefüllte sofort auffallen.
        return row.deletionRequestedAt ? (
          <span className="font-medium text-destructive">
            {new Date(row.deletionRequestedAt).toLocaleDateString()}
          </span>
        ) : null
      default: return null
    }
  }

  const meta = result?.meta
  const rows = result?.data ?? []
  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length > 0 ? 1 : 0) + 1

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
            <ShieldCheck size={26} className="text-primary" />
            Mandanten
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Betreiber-Konsole — Module freischalten, Plätze setzen, Testphasen verwalten.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/loeschprotokoll"
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
          >
            <ScrollText size={15} /> Löschprotokoll
          </Link>
          <Button onClick={() => setAnlegen(true)}>
            <Plus size={16} className="mr-1.5" /> {t('operator.mandantAnlegen.titel')}
          </Button>
        </div>
      </div>

      {anlegen && (
        <MandantAnlegen
          onClose={() => setAnlegen(false)}
          // Ganz neu laden statt nur zu navigieren: Die Firmenauswahl der
          // Seitenleiste liest ihre Liste einmal beim Start (AP-38-B).
          onFertig={(m) => window.location.assign(`/admin/mandanten/${m.id}`)}
        />
      )}

      <FilterBar
        availableFields={AVAILABLE_FILTER_FIELDS}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="operator-tenants"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
      />

      <div className="relative rounded-lg border border-border bg-card shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title="Spalten konfigurieren"
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {} }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColState(next)
              }}
            />
          </div>
        )}

        {error && (
          <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Fixe erste Spalte: der Mandantenname */}
                <th
                  className="relative px-4 py-1.5 text-left font-medium text-foreground"
                  style={colState.columnWidths['name'] ? { width: colState.columnWidths['name'] } : undefined}
                >
                  <span className="flex items-center gap-1">
                    Mandant
                    <ArrowUpDown className="h-3 w-3 opacity-30" />
                  </span>
                  <div
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                    onMouseDown={(e) => handleResizeStart(e, 'name')}
                  />
                </th>

                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground
                      ${col.align === 'right' ? 'text-right' : 'text-left'}
                      ${columnConfigMode ? 'cursor-grab' : ''}
                      ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className="flex items-center gap-1">
                      <span>{col.label}</span>
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() => updateColState((prev) => ({
                            ...prev,
                            columnKeys: prev.columnKeys.filter((k) => k !== col.key),
                          }))}
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          title="Spalte entfernen"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                    />
                  </th>
                ))}

                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                  </th>
                )}

                <th className="w-4" />
              </tr>
            </thead>

            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">Laden…</td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Keine Mandanten gefunden.
                  </td>
                </tr>
              )}

              {rows.map((row, idx) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/admin/mandanten/${row.id}`)}
                  className={`cursor-pointer border-b border-border last:border-0 ${
                    idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  } hover:bg-accent/30 transition-colors`}
                >
                  <td className="px-4 py-1.5 font-medium text-foreground whitespace-nowrap">
                    {row.name}
                    {row.isOperator && (
                      <span className="ml-2 rounded bg-primary/10 px-1 text-[10px] font-medium text-primary">
                        Betreiber
                      </span>
                    )}
                  </td>
                  {shownColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-1.5 whitespace-nowrap ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}
                    >
                      {renderCell(row, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}

              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="px-4 py-1.5 whitespace-nowrap text-muted-foreground">
                    {loading ? 'Laden…' : `${meta?.total ?? rows.length} Mandanten`}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {/* Aktive Benutzer über alle Mandanten der Seite — die Zahl, die
                          am ehesten einer Rechnung entspricht. */}
                      {col.key === 'activeUsers' ? rows.reduce((s, r) => s + r.activeUsers, 0) : ''}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>Seite {meta.page} von {meta.totalPages} ({meta.total} Einträge)</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >‹</button>
              <button
                type="button"
                disabled={page >= meta.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
